import {
  convertFormulaDelimiter,
  normalizeFormulaWhitespace,
} from './formula_locale_normalizer';

export const FORMULA_DELIMITER_STORAGE_KEY = 'bettersheets_formula_argument_delimiter';
export const FORMULA_DELIMITER_CHANGED = 'bettersheets:formula-delimiter-changed';

/**
 * @returns {'comma' | 'semicolon'}
 */
export function getFormulaArgumentDelimiter() {
  if (typeof localStorage === 'undefined') return 'comma';
  const stored = localStorage.getItem(FORMULA_DELIMITER_STORAGE_KEY);
  return stored === 'semicolon' ? 'semicolon' : 'comma';
}

/**
 * @param {'comma' | 'semicolon'} value
 */
export function setFormulaArgumentDelimiter(value) {
  if (typeof localStorage === 'undefined') return;
  localStorage.setItem(
    FORMULA_DELIMITER_STORAGE_KEY,
    value === 'semicolon' ? 'semicolon' : 'comma'
  );
}

/**
 * @param {string} formula
 * @returns {string}
 */
export function toCanonicalFormula(formula) {
  return convertFormulaDelimiter(normalizeFormulaWhitespace(formula), 'comma');
}

/**
 * @param {string} formula
 * @param {'comma' | 'semicolon'} [delimiter]
 * @returns {string}
 */
export function formatFormulaForArgumentDelimiter(formula, delimiter = getFormulaArgumentDelimiter()) {
  const canonical = toCanonicalFormula(formula);
  if (delimiter === 'semicolon') {
    return convertFormulaDelimiter(canonical, 'semicolon');
  }
  return canonical;
}

/**
 * @param {string} rawFormula
 * @returns {{ canonical: string, display: string }}
 */
export function processGeneratedFormula(rawFormula) {
  const canonical = toCanonicalFormula(rawFormula);
  const display = formatFormulaForArgumentDelimiter(canonical);
  return { canonical, display };
}

/**
 * @returns {string}
 */
export function delimiterPromptInstruction() {
  return getFormulaArgumentDelimiter() === 'semicolon'
    ? ' Use semicolon (;) as the function argument separator, not comma.'
    : '';
}

/**
 * @param {import('@hotwired/stimulus').Controller} controller
 * @param {string} display
 */
export function applyDisplayFormula(controller, display) {
  controller.formulaValue = display;

  if (controller.hasFormulaTextTarget) {
    controller.formulaTextTarget.textContent = display;
    return;
  }

  if (controller.hasOutputTarget) {
    const formulaEl = controller.outputTarget.querySelector('p.font-mono');
    if (formulaEl) {
      formulaEl.textContent = display;
    } else {
      controller.outputTarget.textContent = display;
    }
  }
}

/**
 * @param {import('@hotwired/stimulus').Controller} controller
 */
export function refreshFromCanonical(controller) {
  if (!controller.canonicalFormula) return;
  const display = formatFormulaForArgumentDelimiter(controller.canonicalFormula);
  applyDisplayFormula(controller, display);
}

/**
 * @param {import('@hotwired/stimulus').Controller} controller
 */
export function bindFormulaDelimiterListener(controller) {
  controller._formulaDelimiterHandler = () => {
    refreshFromCanonical(controller);
  };
  window.addEventListener(FORMULA_DELIMITER_CHANGED, controller._formulaDelimiterHandler);
}

/**
 * @param {import('@hotwired/stimulus').Controller} controller
 */
export function unbindFormulaDelimiterListener(controller) {
  if (!controller._formulaDelimiterHandler) return;
  window.removeEventListener(FORMULA_DELIMITER_CHANGED, controller._formulaDelimiterHandler);
  controller._formulaDelimiterHandler = null;
}
